import { Component, Input } from '@angular/core';
import type { RatingOverview } from '../../../../services/sentiment-analysis-stats/sentiment-analysis-stats.service';

@Component({
  selector: 'app-sentiment-overview-widget',
  standalone: false,
  templateUrl: './sentiment-overview-widget.component.html',
  styleUrls: ['./sentiment-overview-widget.component.scss'],
})
export class SentimentOverviewWidgetComponent {
  @Input() sentiment: {
    positive: { count: number; percentage: number };
    neutral: { count: number; percentage: number };
    negative: { count: number; percentage: number };
  } = {
    positive: { count: 0, percentage: 0 },
    neutral: { count: 0, percentage: 0 },
    negative: { count: 0, percentage: 0 },
  };
  /** Legacy stats path when `ratingOverview` is not provided. */
  @Input() npsScore: number = 0;
  @Input() sentimentScore: number = 0;
  /** Insights overview: NPS vs CSAT primary score + segments for the rating donut. */
  @Input() ratingOverview: RatingOverview | null = null;

  hasNoData(): boolean {
    return (
      (this.sentiment?.positive?.count || 0) === 0 &&
      (this.sentiment?.neutral?.count || 0) === 0 &&
      (this.sentiment?.negative?.count || 0) === 0
    );
  }

  get primaryRatingLabel(): string {
    return this.ratingOverview?.type === 'csat' ? 'CSAT Score' : 'NPS Score';
  }

  get primaryRatingValue(): number {
    return this.ratingOverview != null ? this.ratingOverview.score : this.npsScore;
  }

  get primaryRatingDigits(): string {
    if (this.ratingOverview == null) {
      return '1.0-0';
    }
    return this.ratingOverview.type === 'csat' ? '1.0-2' : '1.0-0';
  }

  get primaryRatingHint(): string {
    return this.ratingOverview?.type === 'csat'
      ? '% of top two boxes (scores 4–5)'
      : '% promoters − % detractors';
  }

  get primaryRatingGreen(): boolean {
    if (this.ratingOverview?.type === 'csat') {
      return this.ratingOverview.score >= 70;
    }
    return this.primaryRatingValue >= 0;
  }

  get primaryRatingAmber(): boolean {
    if (this.ratingOverview?.type !== 'csat') return false;
    const s = this.ratingOverview.score;
    return s >= 40 && s < 70;
  }

  get primaryRatingRed(): boolean {
    if (this.ratingOverview?.type === 'csat') {
      return this.ratingOverview.score < 40;
    }
    return this.primaryRatingValue < 0;
  }

  get ratingDonutTitle(): string {
    return this.ratingOverview?.type === 'csat' ? 'CSAT distribution' : 'NPS distribution';
  }

  get showRatingDonutColumn(): boolean {
    return this.ratingOverview != null;
  }

  getDonutChartData(): {
    positive: { dashArray: string; offset: string };
    neutral: { dashArray: string; offset: string };
    negative: { dashArray: string; offset: string };
  } | null {
    const s = this.sentiment;
    const total = (s.positive.count || 0) + (s.neutral.count || 0) + (s.negative.count || 0);
    if (total === 0) return null;
    const circumference = 314.16;
    const positiveDash = ((s.positive.percentage || 0) / 100) * circumference;
    const neutralDash = ((s.neutral.percentage || 0) / 100) * circumference;
    const negativeDash = ((s.negative.percentage || 0) / 100) * circumference;
    return {
      positive: { dashArray: `${positiveDash} ${circumference}`, offset: '0' },
      neutral: { dashArray: `${neutralDash} ${circumference}`, offset: `-${positiveDash}` },
      negative: { dashArray: `${negativeDash} ${circumference}`, offset: `-${positiveDash + neutralDash}` },
    };
  }

  /** Exposed for template so donut bindings are always defined when shown. */
  get donutData(): {
    positive: { dashArray: string; offset: string };
    neutral: { dashArray: string; offset: string };
    negative: { dashArray: string; offset: string };
  } {
    const d = this.getDonutChartData();
    const c = 314.16;
    return (
      d ?? {
        positive: { dashArray: `0 ${c}`, offset: '0' },
        neutral: { dashArray: `0 ${c}`, offset: '0' },
        negative: { dashArray: `0 ${c}`, offset: '0' },
      }
    );
  }

  getRatingDonutChartData(): {
    seg0: { dashArray: string; offset: string };
    seg1: { dashArray: string; offset: string };
    seg2: { dashArray: string; offset: string };
  } | null {
    const ro = this.ratingOverview;
    if (!ro?.segments?.length) return null;
    const totalCount = ro.segments.reduce((acc, s) => acc + (s.count || 0), 0);
    if (totalCount === 0) return null;
    const circumference = 314.16;
    const p0 = ((ro.segments[0]?.percentage || 0) / 100) * circumference;
    const p1 = ((ro.segments[1]?.percentage || 0) / 100) * circumference;
    const p2 = ((ro.segments[2]?.percentage || 0) / 100) * circumference;
    return {
      seg0: { dashArray: `${p0} ${circumference}`, offset: '0' },
      seg1: { dashArray: `${p1} ${circumference}`, offset: `-${p0}` },
      seg2: { dashArray: `${p2} ${circumference}`, offset: `-${p0 + p1}` },
    };
  }

  get ratingDonutData(): {
    seg0: { dashArray: string; offset: string };
    seg1: { dashArray: string; offset: string };
    seg2: { dashArray: string; offset: string };
  } {
    const d = this.getRatingDonutChartData();
    const c = 314.16;
    return (
      d ?? {
        seg0: { dashArray: `0 ${c}`, offset: '0' },
        seg1: { dashArray: `0 ${c}`, offset: '0' },
        seg2: { dashArray: `0 ${c}`, offset: '0' },
      }
    );
  }
}
